/**
 * Minimal VOD playlist reader.
 *
 * The prefetcher needs two things a playlist states plainly: the
 * complete list of segments, and where each one sits on the programme
 * clock. The second is what lets a seek be answered with the segments
 * around the new playhead rather than with the next N in file order,
 * so the durations are read rather than assumed.
 *
 * The Worker has already rewritten every media line into a fully
 * signed path, so a line is taken exactly as it stands.
 */

export interface PlaylistSegment {
  /** Ordinal within the playlist, from zero. */
  index: number;
  /** Signed URL as the playlist states it. */
  url: string;
  /** Start on the programme clock, in seconds. */
  startSeconds: number;
  durationSeconds: number;
  endSeconds: number;
}

/**
 * Throws on a playlist that does not declare a duration for every
 * segment: a silent zero would put every segment at the same instant
 * and quietly wreck the seek heuristic. The caller treats a throw as
 * "no local cache for this angle" and playback is untouched either way.
 */
export function parsePlaylist(body: string): PlaylistSegment[] {
  const segments: PlaylistSegment[] = [];
  let pendingDuration: number | null = null;
  let clock = 0;

  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (line === '') {
      continue;
    }
    if (line.startsWith('#')) {
      if (line.startsWith('#EXTINF:')) {
        const value = Number.parseFloat(line.slice('#EXTINF:'.length));
        if (!Number.isFinite(value) || value < 0) {
          throw new Error(`playlist has a malformed EXTINF: ${line}`);
        }
        pendingDuration = value;
      }
      continue;
    }
    if (pendingDuration === null) {
      throw new Error(`playlist has a segment without a preceding EXTINF: ${line}`);
    }
    segments.push({
      index: segments.length,
      url: line,
      startSeconds: clock,
      durationSeconds: pendingDuration,
      endSeconds: clock + pendingDuration,
    });
    clock += pendingDuration;
    pendingDuration = null;
  }

  return segments;
}

/**
 * Index of the segment covering an instant, clamped into the playlist
 * at both ends so a playhead past the last segment still answers with
 * a real segment rather than nothing.
 */
export function segmentIndexAt(segments: readonly PlaylistSegment[], seconds: number): number {
  if (segments.length === 0) {
    return -1;
  }
  if (seconds <= 0) {
    return 0;
  }
  for (const segment of segments) {
    if (seconds < segment.endSeconds) {
      return segment.index;
    }
  }
  return segments.length - 1;
}
